import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Table,
  message,
  Divider,
  Row,
  Col,
  Card,
  Space
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SendOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import invoiceService, { Invoice, InvoiceItem, InvoiceOption, CreateInvoiceInput } from '../../../../services/invoiceService';
import invoiceItemTemplateService, { InvoiceItemTemplate } from '../../../../services/invoiceItemTemplateService';
import apiService from '../../../../services/apiService';
import { useDraggableRows } from '../../../../components/table/useDraggableRows';
import hrService, { Department } from '@/services/hrService';
import { useTranslation } from "react-i18next";
import { findSalesJournal } from '../../utils/salesJournal';

const { Option } = Select;
const { TextArea } = Input;

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'HKD', label: 'HKD - Hong Kong Dollar', symbol: 'HKD ' },
  { value: 'USD', label: 'USD - US Dollar', symbol: 'USD ' },
  { value: 'CNY', label: 'CNY - Chinese Yuan', symbol: 'CNY¥' },
  { value: 'EUR', label: 'EUR - Euro', symbol: 'EUR€' },
  { value: 'GBP', label: 'GBP - British Pound', symbol: 'GBP£' },
  { value: 'JPY', label: 'JPY - Japanese Yen', symbol: 'JPY¥' },
  { value: 'SGD', label: 'SGD - Singapore Dollar', symbol: 'SGD$' },
  { value: 'TWD', label: 'TWD - Taiwan Dollar', symbol: 'TWD$' },
];

// Default unit options
const DEFAULT_UNIT_OPTIONS = ['unit(s)', 'Rack(s)', 'Store', 'Month(s)', 'Order(s)', 'Hour(s)'];
const DEFAULT_PAYMENT_PERIOD_OPTIONS = ['7 Days', 'Net 30', 'Net 60', 'Due on Receipt', 'COD', '50% Deposit'];

// Merge built-in options with any persisted in company settings, preserving
// the built-ins first and keeping order stable. Used by both the Unit and
// Payment Period pickers so user-added values survive a page reload.
const mergeOptions = (defaults: string[], persisted: string[] | undefined | null): string[] => {
  const seen = new Set(defaults);
  const extras = (persisted || []).filter(v => !!v && !seen.has(v));
  return [...defaults, ...extras];
};

interface InvoiceFormProps {
  visible: boolean;
  invoice?: Invoice | null;
  onClose: () => void;
  onSuccess: () => void;
}

// Round to 2 decimal places, half-away-from-zero — matches how toLocaleString({maximumFractionDigits:2})
// displays values, so summing rounded line totals matches the displayed grand total to the cent.
// Number.EPSILON corrects the IEEE-754 case where x.x25 is stored as x.x2499... and would otherwise
// round down (e.g. 492.525 -> 492.52 instead of the displayed 492.53).
const round2 = (n: number): number => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Exchange rates round-trip through inverse storage (Decimal(15,6)) which can introduce
// tiny precision wobble past the 4th decimal (e.g. 7.845 -> 7.844983). Rates are usually
// quoted to 4dp anyway, so display/store at that resolution to hide the artifact.
const round4 = (n: number): number => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;

interface LineItem extends Omit<InvoiceItem, 'id' | 'invoiceId' | 'createdAt' | 'updatedAt'> {
  key: string;
}

interface OptionItem {
  key: string;
  category?: string;
  subcategory?: string;
  description: string;
  itemCode?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  discount: number;
  discountType: 'fixed' | 'percentage';
  lineTotal: number;
  accountId?: string;
  departmentId?: string;
  departmentName?: string;
  sortOrder?: number;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ visible, invoice, onClose, onSuccess }) => {
    const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [optionItems, setOptionItems] = useState<OptionItem[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [templates, setTemplates] = useState<InvoiceItemTemplate[]>([]);
  // Departments for the line-item analytic dimension, mirroring JournalEntryForm.
  const [departments, setDepartments] = useState<Department[]>([]);

  // Departments are still fetched, but ONLY to render the name of a line saved
  // before this field was unlinked from HR. They are never offered as choices.
  const legacyDepartmentName = (departmentId?: string): string => {
    if (!departmentId) return '';
    const d = departments.find(x => x.id === departmentId);
    return d ? `${d.code} - ${d.name}` : '';
  };
  const [totalAmount, setTotalAmount] = useState(0);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [journals, setJournals] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [savePaymentTermForCustomer, setSavePaymentTermForCustomer] = useState(false);
  const [accountingSettings, setAccountingSettings] = useState<any>(null);
  const [customInvoiceNo, setCustomInvoiceNo] = useState<string>('');
  const [invoiceNoValidating, setInvoiceNoValidating] = useState(false);
  const [invoiceNoError, setInvoiceNoError] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('HKD');
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState<string[]>(DEFAULT_UNIT_OPTIONS);
  const [paymentPeriodOptions, setPaymentPeriodOptions] = useState<string[]>(DEFAULT_PAYMENT_PERIOD_OPTIONS);
  const [newUnitInput, setNewUnitInput] = useState<string>('');
  const [newPaymentPeriodInput, setNewPaymentPeriodInput] = useState<string>('');

  // Re-hydrate Unit + Payment Period dropdowns from accounting_settings whenever
  // settings finish loading, so values another user added on this company show up.
  useEffect(() => {
    setUnitOptions(mergeOptions(DEFAULT_UNIT_OPTIONS, accountingSettings?.unitOptions));
    setPaymentPeriodOptions(mergeOptions(DEFAULT_PAYMENT_PERIOD_OPTIONS, accountingSettings?.paymentPeriodOptions));
  }, [accountingSettings]);

  // Drag-and-drop row reordering for the line-items table. sortOrder gets
  // reassigned on save (buildInvoiceData maps over the array with index).
  const { onRow: lineItemsRowDrag, dragHandleColumn: lineItemsDragHandleColumn } =
    useDraggableRows(lineItems, setLineItems);

  // Persist a newly-typed Unit or Payment Period value back to company settings,
  // so it survives reload and appears for other users in the same company.
  // Idempotent — silently no-ops if the value is already in the persisted list.
  const persistCompanyOption = async (kind: 'unitOptions' | 'paymentPeriodOptions', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const current: string[] = (accountingSettings?.[kind] as string[]) || [];
    if (current.includes(trimmed)) return;
    const updated = [...current, trimmed];
    try {
      await apiService.put('/accounting/settings', { [kind]: updated });
      setAccountingSettings({ ...(accountingSettings || {}), [kind]: updated });
    } catch (err) {
      console.error(`Failed to persist ${kind}:`, err);
    }
  };

  // Counterpart to persistCompanyOption — remove a user-added option from
  // both local state and company settings. Built-in defaults are never
  // removable (the × button is hidden for them at render time).
  const removeCompanyOption = async (
    kind: 'unitOptions' | 'paymentPeriodOptions',
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setter(prev => prev.filter(v => v !== trimmed));
    const current: string[] = (accountingSettings?.[kind] as string[]) || [];
    const updated = current.filter(v => v !== trimmed);
    try {
      await apiService.put('/accounting/settings', { [kind]: updated });
      setAccountingSettings({ ...(accountingSettings || {}), [kind]: updated });
    } catch (err) {
      console.error(`Failed to remove ${kind} option:`, err);
    }
  };

  // Render the contents of an <Option> so user-added (non-default) values
  // carry a small × button that deletes them from the company settings.
  // Using onMouseDown + preventDefault stops AntD from committing the value
  // as a selection when the icon is clicked.
  const renderOptionContent = (
    value: string,
    defaults: string[],
    onRemove: (v: string) => void
  ) => {
    const isDefault = defaults.includes(value);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>{value}</span>
        {!isDefault && (
          <CloseCircleOutlined
            style={{ color: '#bbb', fontSize: 12, cursor: 'pointer' }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(value); }}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (visible) {
      loadCustomers();
      loadTemplates();
      loadAccounts();
      loadJournals();
      loadAccountingSettings();
      if (invoice) {
        // Edit mode
        form.setFieldsValue({
          customerId: invoice.customerId,
          invoiceNo: invoice.invoiceNo,
          currency: invoice.currency || 'HKD',
          invoiceDate: invoice.invoiceDate ? dayjs(invoice.invoiceDate) : dayjs(),
          dueDate: invoice.dueDate ? dayjs(invoice.dueDate) : null,
          billReference: invoice.billReference || '',
          paymentTerms: invoice.paymentTerms || '',
          paymentTermsText: invoice.paymentTermsText || '',
          termsConditions: invoice.termsConditions || '',
          contactPerson: invoice.contactPerson || '',
          contactEmail: invoice.contactEmail || '',
          contactPhone: invoice.contactPhone || '',
          deliveryAddress: invoice.deliveryAddress || '',
          billingAddress: invoice.billingAddress || '',
          status: invoice.status
        });
        setCustomInvoiceNo(invoice.invoiceNo);
        const editCurrency = invoice.currency || 'HKD';
        setSelectedCurrency(editCurrency);
        if ((invoice as any).exchangeRate) {
          setExchangeRate(round4(Number((invoice as any).exchangeRate)));
        } else if (editCurrency !== 'HKD') {
          fetchExchangeRate(editCurrency);
        } else {
          setExchangeRate(null);
        }
        const items: LineItem[] = invoice.items.map((item, index) => ({
          key: `item-${index}`,
          description: item.description || '',
          itemCode: item.itemCode,
          quantity: Number(item.quantity || 0),
          unit: item.unit || undefined,
          unitPrice: Number(item.unitPrice || 0),
          discount: Number(item.discount || 0),
          discountType: (item.discountType as 'fixed' | 'percentage') || 'fixed',
          taxRate: Number(item.taxRate || 0),
          taxAmount: Number(item.taxAmount || 0),
          lineTotal: Number(item.lineTotal || 0),
          accountId: item.accountId,
          departmentId: item.departmentId,
          departmentName: (item as any).departmentName ?? undefined,
          sortOrder: item.sortOrder || index
        }));
        setLineItems(items);

        // Load options
        const opts: OptionItem[] = (invoice.options || []).map((opt, index) => ({
          key: `opt-${index}`,
          category: opt.category || undefined,
          subcategory: opt.subcategory || undefined,
          description: opt.description || '',
          itemCode: opt.itemCode || undefined,
          quantity: Number(opt.quantity || 0),
          unit: opt.unit || undefined,
          unitPrice: Number(opt.unitPrice || 0),
          discount: Number(opt.discount || 0),
          discountType: (opt.discountType as 'fixed' | 'percentage') || 'percentage',
          lineTotal: Number(opt.lineTotal || 0),
          accountId: opt.accountId || undefined,
          sortOrder: opt.sortOrder || index
        }));
        setOptionItems(opts);
        calculateTotal(items, opts);
      } else {
        // Create mode
        form.resetFields();
        form.setFieldsValue({
          invoiceDate: dayjs(),
          currency: 'HKD'
        });
        setLineItems([]);
        setOptionItems([]);
        setTotalAmount(0);
        setSelectedCustomer(null);
        setSavePaymentTermForCustomer(false);
        setCustomInvoiceNo('');
        setInvoiceNoError('');
        setSelectedCurrency('HKD');
        setExchangeRate(null);
      }
    }
  }, [visible, invoice, form]);

  // For NEW invoices, apply company-wide defaults from Accounting Settings as soon
  // as settings finish loading (the visible/init effect above runs synchronously
  // before the async fetch resolves, so without this the defaults wouldn't show up
  // until the user picks a customer). Only fills fields the user hasn't touched.
  // Note: settings.invoiceDefaultPaymentTerms is the long-form text and belongs in
  // the "Payment Terms" textarea (paymentTermsText), NOT the "Payment Period"
  // dropdown (paymentTerms).
  useEffect(() => {
    if (!visible || invoice || !accountingSettings) return;
    const current = form.getFieldsValue(['paymentTermsText', 'termsConditions']);
    const next: Record<string, string> = {};
    if (!current.paymentTermsText && accountingSettings.invoiceDefaultPaymentTerms) {
      next.paymentTermsText = accountingSettings.invoiceDefaultPaymentTerms;
    }
    if (!current.termsConditions && accountingSettings.invoiceDefaultTermsConditions) {
      next.termsConditions = accountingSettings.invoiceDefaultTermsConditions;
    }
    if (Object.keys(next).length > 0) form.setFieldsValue(next);
  }, [visible, invoice, accountingSettings, form]);

  // Fetch exchange rate when currency changes
  const fetchExchangeRate = async (currencyCode: string) => {
    if (currencyCode === 'HKD') {
      setExchangeRate(null);
      return;
    }
    setExchangeRateLoading(true);
    try {
      const response: any = await apiService.post('/accounting/currencies/convert', {
        fromCurrencyCode: currencyCode,
        toCurrencyCode: 'HKD',
        amount: 1
      });
      if (response.data?.rate) {
        setExchangeRate(round4(response.data.rate));
      } else {
        setExchangeRate(null);
        message.warning(t('No exchange rate found for {{currencyCode}}. Please set it in Accounting → Advanced → Currency Management.', { currencyCode }));
      }
    } catch {
      setExchangeRate(null);
      message.warning(t('No exchange rate found for {{currencyCode}}. Please set it in Accounting → Advanced → Currency Management.', { currencyCode }));
    } finally {
      setExchangeRateLoading(false);
    }
  };

  const handleCurrencyChange = (value: string) => {
    setSelectedCurrency(value);
    fetchExchangeRate(value);
  };

  /**
   * Check the {YYYY}/{MM} segment of a typed invoice number.
   *
   * Numbers look like INV2026/08/0001 or INV/SL/2026/08/0001 — the last three
   * "/"-separated parts are year, month and sequence. Returns an error string,
   * or '' when it looks fine. A number that doesn't carry a year/month at all
   * is left alone: staff still key in legacy and one-off formats, and rejecting
   * those would block real work.
   */
  const validateInvoiceNoPeriod = (invoiceNo: string): string => {
    const parts = invoiceNo.trim().split('/');
    if (parts.length < 3) return '';

    const seq = parts[parts.length - 1];
    const month = parts[parts.length - 2];
    const year = parts[parts.length - 3].replace(/^\D+/, ''); // strip "INV" from "INV2026"

    if (!/^\d{4}$/.test(year) || !/^\d{1,2}$/.test(month) || !/^\d+$/.test(seq)) return '';

    const y = Number(year);
    const m = Number(month);

    if (m < 1 || m > 12) return t('Invalid month "{{month}}" — must be 01-12.', { month });

    const thisYear = new Date().getFullYear();
    if (y < 2000 || y > thisYear + 1) {
      return `Invalid year "${year}" — expected between 2000 and ${thisYear + 1}.`;
    }
    return '';
  };

  const checkInvoiceNoDuplicate = async (invoiceNo: string) => {
    if (!invoiceNo || invoiceNo.trim() === '') {
      setInvoiceNoError('');
      return;
    }
    if (invoice && invoice.invoiceNo === invoiceNo) {
      setInvoiceNoError('');
      return;
    }
    // Catch a bad year/month before spending a round-trip on the duplicate check.
    const periodError = validateInvoiceNoPeriod(invoiceNo);
    if (periodError) {
      setInvoiceNoError(periodError);
      return;
    }
    setInvoiceNoValidating(true);
    try {
      const response: any = await apiService.get(`/accounting/invoices?status=all`);
      const existingInvoice = response.data.find((inv: Invoice) =>
        inv.invoiceNo === invoiceNo && inv.id !== invoice?.id
      );
      if (existingInvoice) {
        setInvoiceNoError(`Invoice number "${invoiceNo}" already exists (Status: ${existingInvoice.status})`);
      } else {
        setInvoiceNoError('');
      }
    } catch (error) {
      console.error('Failed to check invoice number:', error);
    } finally {
      setInvoiceNoValidating(false);
    }
  };

  const handleInvoiceNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setCustomInvoiceNo(value);
    form.setFieldsValue({ invoiceNo: value });
    const timeoutId = setTimeout(() => {
      checkInvoiceNoDuplicate(value);
    }, 500);
    return () => clearTimeout(timeoutId);
  };

  const loadCustomers = async () => {
    try {
      const response: any = await apiService.get('/customers/dropdown');
      setCustomers(response.customers || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
      message.error(t('Failed to load customers'));
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await invoiceItemTemplateService.getActiveTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const response: any = await apiService.get('/accounting/accounts');
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const loadJournals = async () => {
    try {
      const response: any = await apiService.get('/accounting/journals/active');
      setJournals(response.data || []);
    } catch (error) {
      console.error('Failed to load journals:', error);
    }
  };

  const loadAccountingSettings = async () => {
    try {
      const response: any = await apiService.get('/accounting/settings');
      setAccountingSettings(response.data || null);
    } catch (error) {
      console.error('Failed to load accounting settings:', error);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    // Two distinct fields that previously got conflated:
    //   form.paymentTerms     -> "Payment Period" dropdown (Net 30 / 7 Days / ...)
    //   form.paymentTermsText -> "Payment Terms" long-form textarea
    // The customer's saved `defaultPaymentTerms` is a short Payment Period value;
    // the company-wide setting `invoiceDefaultPaymentTerms` is long-form text that
    // belongs in the textarea.
    const next: Record<string, string> = {};
    if (customer?.defaultPaymentTerms) next.paymentTerms = customer.defaultPaymentTerms;
    if (accountingSettings?.invoiceDefaultPaymentTerms) next.paymentTermsText = accountingSettings.invoiceDefaultPaymentTerms;
    if (accountingSettings?.invoiceDefaultTermsConditions) next.termsConditions = accountingSettings.invoiceDefaultTermsConditions;
    if (Object.keys(next).length > 0) form.setFieldsValue(next);
  };

  const handleTemplateSelect = (key: string, templateId: string) => {
    const selectedTemplate = templates.find(t => t.id === templateId);
    if (selectedTemplate) {
      const newItems = lineItems.map(item => {
        if (item.key === key) {
          const unitPrice = Number(selectedTemplate.unitPrice || 0);
          const quantity = item.quantity || 1;
          const accountId = selectedTemplate.accountId || item.accountId;
          return {
            ...item,
            itemCode: selectedTemplate.itemCode,
            description: selectedTemplate.description,
            unitPrice,
            lineTotal: round2(quantity * unitPrice),
            accountId
          };
        }
        return item;
      });
      setLineItems(newItems);
      calculateTotal(newItems, optionItems);
    }
  };

  const handleOptionTemplateSelect = (key: string, templateId: string) => {
    const selectedTemplate = templates.find(t => t.id === templateId);
    if (selectedTemplate) {
      const newOptions = optionItems.map(opt => {
        if (opt.key === key) {
          const unitPrice = Number(selectedTemplate.unitPrice || 0);
          const quantity = opt.quantity || 1;
          return {
            ...opt,
            itemCode: selectedTemplate.itemCode,
            description: selectedTemplate.description,
            unitPrice,
            lineTotal: round2(quantity * unitPrice),
            accountId: selectedTemplate.accountId || opt.accountId
          };
        }
        return opt;
      });
      setOptionItems(newOptions);
      calculateTotal(lineItems, newOptions);
    }
  };

  // --- Line Items ---
  // Departments for the line-item Department column. Same source and inline
  // "create new" behaviour as JournalEntryForm, so the two line-item tables
  // behave identically — which is what the client asked for.
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setDepartments(await hrService.department.getAll());
      } catch (error) {
        console.error('Failed to fetch departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  // Generates a deterministic code from the typed name so the user does not
  // have to deal with the code/name split here; they can refine it later in
  // HR > Departments.

  const addLineItem = () => {
    const defaultRevenueAccount = accounts.find(a => a.code === '4000' && a.accountType === 'REVENUE');
    const newItem: LineItem = {
      key: `item-${Date.now()}`,
      description: '',
      itemCode: '',
      quantity: 1,
      unit: 'unit(s)',
      unitPrice: 0,
      discount: 0,
      discountType: 'fixed',
      taxRate: 0,
      taxAmount: 0,
      lineTotal: 0,
      accountId: defaultRevenueAccount?.id
    };
    const newItems = [...lineItems, newItem];
    setLineItems(newItems);
  };

  const updateLineItem = (key: string, field: string, value: any) => {
    const newItems = lineItems.map(item => {
      if (item.key === key) {
        const updatedItem = { ...item, [field]: value };
        // Recalculate line total when quantity, unitPrice, discount, discountType, or taxRate changes
        if (['quantity', 'unitPrice', 'discount', 'discountType', 'taxRate'].includes(field)) {
          const quantity = Number(updatedItem.quantity) || 0;
          const unitPrice = Number(updatedItem.unitPrice) || 0;
          const discount = Number(updatedItem.discount) || 0;
          const discountType = updatedItem.discountType || 'fixed';
          const taxRate = Number(updatedItem.taxRate) || 0;

          let subtotal = quantity * unitPrice;
          if (discountType === 'percentage') {
            subtotal = subtotal * (1 - discount / 100);
          } else {
            subtotal = subtotal - discount;
          }

          const taxAmount = subtotal * (taxRate / 100);
          updatedItem.taxAmount = round2(taxAmount);
          updatedItem.lineTotal = round2(subtotal + taxAmount);
        }
        return updatedItem;
      }
      return item;
    });
    setLineItems(newItems);
    calculateTotal(newItems, optionItems);
  };

  const removeLineItem = (key: string) => {
    const newItems = lineItems.filter(item => item.key !== key);
    setLineItems(newItems);
    calculateTotal(newItems, optionItems);
  };

  // --- Option Items ---
  const addOptionItem = () => {
    const defaultRevenueAccount = accounts.find(a => a.code === '4000' && a.accountType === 'REVENUE');
    const newOption: OptionItem = {
      key: `opt-${Date.now()}`,
      description: '',
      quantity: 1,
      unit: 'unit(s)',
      unitPrice: 0,
      discount: 0,
      discountType: 'percentage',
      lineTotal: 0,
      accountId: defaultRevenueAccount?.id
    };
    const newOptions = [...optionItems, newOption];
    setOptionItems(newOptions);
  };

  const updateOptionItem = (key: string, field: string, value: any) => {
    const newOptions = optionItems.map(opt => {
      if (opt.key === key) {
        const updatedOpt = { ...opt, [field]: value };
        if (['quantity', 'unitPrice', 'discount', 'discountType'].includes(field)) {
          const quantity = Number(updatedOpt.quantity) || 0;
          const unitPrice = Number(updatedOpt.unitPrice) || 0;
          const discount = Number(updatedOpt.discount) || 0;
          const discountType = updatedOpt.discountType || 'percentage';

          let subtotal = quantity * unitPrice;
          if (discountType === 'percentage') {
            subtotal = subtotal * (1 - discount / 100);
          } else {
            subtotal = subtotal - discount;
          }
          updatedOpt.lineTotal = round2(subtotal);
        }
        return updatedOpt;
      }
      return opt;
    });
    setOptionItems(newOptions);
    calculateTotal(lineItems, newOptions);
  };

  const removeOptionItem = (key: string) => {
    const newOptions = optionItems.filter(opt => opt.key !== key);
    setOptionItems(newOptions);
    calculateTotal(lineItems, newOptions);
  };

  const calculateTotal = (items: LineItem[], options: OptionItem[]) => {
    // Round each line, then sum, then round once more to absorb residual float drift.
    // This guarantees totalAmount equals the sum of the displayed (rounded) line totals.
    const itemsTotal = items.reduce((sum, item) => sum + round2(Number(item.lineTotal || 0)), 0);
    const optionsTotal = options.reduce((sum, opt) => sum + round2(Number(opt.lineTotal || 0)), 0);
    setTotalAmount(round2(itemsTotal + optionsTotal));
  };

  const buildInvoiceData = (values: any, statusOverride?: string): CreateInvoiceInput => {
    return {
      customerId: values.customerId,
      invoiceNo: customInvoiceNo || undefined,
      invoiceDate: values.invoiceDate ? values.invoiceDate.format('YYYY-MM-DD') : undefined,
      totalAmount,
      currency: selectedCurrency,
      exchangeRate: selectedCurrency !== 'HKD' && exchangeRate ? exchangeRate : undefined,
      status: (statusOverride || (invoice ? invoice.status : 'DRAFT')) as 'DRAFT' | 'CONFIRM' | 'SENT',
      dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : undefined,
      billReference: values.billReference || undefined,
      // Send '' (not undefined) when the user clears these fields, so the backend
      // actually writes the empty value instead of silently dropping the key and
      // leaving the previous content in place — fixes the bug where deleting T&C
      // (or payment terms) and saving made the old text re-appear on next open.
      paymentTerms: values.paymentTerms ?? '',
      paymentTermsText: values.paymentTermsText ?? '',
      termsConditions: values.termsConditions ?? '',
      deliveryAddress: values.deliveryAddress || undefined,
      billingAddress: values.billingAddress || undefined,
      contactPerson: values.contactPerson || undefined,
      contactEmail: values.contactEmail || undefined,
      contactPhone: values.contactPhone || undefined,
      items: lineItems.map((item, index) => ({
        description: item.description,
        itemCode: item.itemCode,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice,
        discount: item.discount || 0,
        discountType: item.discountType || 'fixed',
        taxRate: item.taxRate || 0,
        taxAmount: item.taxAmount || 0,
        lineTotal: item.lineTotal,
        accountId: item.accountId,
        departmentId: item.departmentId,
        // The Department cell is free text now, so the typed value has to travel
        // with the line. Without this the name never left the browser: the save
        // dropped it and the line fell back to the old HR-linked name (or blank).
        departmentName: item.departmentName ?? null,
        sortOrder: index
      })),
      options: optionItems.length > 0 ? optionItems.map((opt, index) => ({
        category: opt.category,
        subcategory: opt.subcategory,
        description: opt.description,
        itemCode: opt.itemCode,
        quantity: opt.quantity,
        unit: opt.unit,
        unitPrice: opt.unitPrice,
        discount: opt.discount || 0,
        discountType: opt.discountType || 'percentage',
        lineTotal: opt.lineTotal,
        accountId: opt.accountId,
        sortOrder: index
      })) : undefined
    };
  };

  const performSubmit = async (createOnly: boolean, values: any) => {
    setLoading(true);

    try {
      if (savePaymentTermForCustomer && selectedCustomer && values.paymentTerms) {
        try {
          await apiService.put(`/customers/${selectedCustomer.id}`, {
            defaultPaymentTerms: values.paymentTerms
          });
        } catch (error) {
          console.error('Failed to save payment terms to customer:', error);
        }
      }

      if (customInvoiceNo && invoiceNoError) {
        message.error(invoiceNoError);
        setLoading(false);
        return;
      }

      const invoiceData = buildInvoiceData(values);

      if (invoice?.id) {
        const updateData: any = { ...invoiceData };
        if (customInvoiceNo && (invoice.status === 'DRAFT' || invoice.status === 'CONFIRM')) {
          updateData.invoiceNo = customInvoiceNo;
        }
        await invoiceService.updateInvoice(invoice.id, updateData);
        message.success(t('Invoice updated successfully'));
      } else {
        const newInvoice = await invoiceService.createInvoice(invoiceData);

        if (!createOnly) {
          // Prefer Default Accounts from settings, fall back to code heuristic
          const arById = accountingSettings?.defaultReceivableAccountId
            ? accounts.find(a => a.id === accountingSettings.defaultReceivableAccountId)
            : null;
          const revenueById = accountingSettings?.defaultRevenueAccountId
            ? accounts.find(a => a.id === accountingSettings.defaultRevenueAccountId)
            : null;
          const arAccount = arById
            || accounts.find(a => a.code === '124000')
            || accounts.find(a => a.code?.startsWith('1200'));
          const revenueAccount = revenueById
            || accounts.find(a => a.code?.includes('4000') || a.code?.startsWith('41'));
          const salesJournal = findSalesJournal(journals);

          if (!arAccount || !revenueAccount || !salesJournal) {
            message.error(t('Missing required accounts or journal. Please ensure Accounts Receivable, Revenue, and Sales/Invoice Journal exist.'));
            setLoading(false);
            return;
          }

          try {
            await invoiceService.postInvoice(newInvoice.id, {
              accountsReceivableAccountId: arAccount.id,
              revenueAccountId: revenueAccount.id,
              journalId: salesJournal.id
            });
            message.success(t('Invoice created and posted successfully'));
          } catch (error: any) {
            message.error(t('Invoice created but failed to post: {{message}}', { message: error.message }));
          }
        } else {
          message.success(t('Invoice created successfully'));
        }
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(t('Failed to save invoice: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (createOnly: boolean = false) => {
    try {
      const values = await form.validateFields();

      if (lineItems.length === 0) {
        message.error(t('Please add at least one line item'));
        return;
      }

      const invalidItems = lineItems.filter(item => !item.description?.trim() || item.quantity <= 0 || item.unitPrice === undefined || item.unitPrice === null);
      if (invalidItems.length > 0) {
        message.error(t('Please enter a description for all line items and ensure quantity is valid'));
        return;
      }

      if (invoice && invoice.status !== 'DRAFT') {
        Modal.confirm({
          title: t('Edit Sent Invoice'),
          content: t('This invoice has already been sent/posted (Status: {{status}}). Are you sure you want to edit it? This may affect accounting records.', { status: invoice.status }),
          okText: t('Yes, Update'),
          cancelText: t('Cancel'),
          onOk: async () => {
            await performSubmit(createOnly, values);
          }
        });
        return;
      }

      await performSubmit(createOnly, values);
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(t('Failed to save invoice: {{message}}', { message: error.message }));
    }
  };

  const handleSubmitConfirm = async () => {
    try {
      const values = await form.validateFields();

      if (lineItems.length === 0) {
        message.error(t('Please add at least one line item'));
        return;
      }

      const invalidItems = lineItems.filter(item => !item.description?.trim() || item.quantity <= 0 || item.unitPrice === undefined || item.unitPrice === null);
      if (invalidItems.length > 0) {
        message.error(t('Please enter a description for all line items and ensure quantity is valid'));
        return;
      }

      await performSubmitWithStatus('CONFIRM', values);
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(t('Failed to save invoice: {{message}}', { message: error.message }));
    }
  };

  const performSubmitWithStatus = async (status: string, values: any) => {
    setLoading(true);

    try {
      if (savePaymentTermForCustomer && selectedCustomer && values.paymentTerms) {
        try {
          await apiService.put(`/customers/${selectedCustomer.id}`, {
            defaultPaymentTerms: values.paymentTerms
          });
        } catch (error) {
          console.error('Failed to save payment terms to customer:', error);
        }
      }

      if (customInvoiceNo && invoiceNoError) {
        message.error(invoiceNoError);
        setLoading(false);
        return;
      }

      const invoiceData = buildInvoiceData(values, status);
      await invoiceService.createInvoice(invoiceData);
      message.success(t('Invoice saved as {{status}}', { status }));
      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(t('Failed to save invoice: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  // All active accounts available for invoice line items
  const availableAccounts = accounts;

  const currencySymbol = CURRENCY_OPTIONS.find(c => c.value === selectedCurrency)?.symbol || '$';

  const lineItemColumns: ColumnsType<LineItem> = [
    {
      title: t('Template'),
      key: 'template',
      width: 150,
      render: (_, record) => (
        <Select
          placeholder={t('Select template')}
          allowClear
          showSearch
          optionFilterProp="children"
          onChange={(val) => val && handleTemplateSelect(record.key, val)}
          style={{ width: '100%' }}
          size="small"
          filterOption={(input, option) =>
            ((option?.children as any) || '').toLowerCase().includes(input.toLowerCase())
          }
        >
          {templates.map(template => (
            <Option key={template.id} value={template.id}>
              {template.itemCode} - {template.description}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      width: 240,
      // TextArea + autoSize lets long descriptions wrap and grow instead of
      // clipping inside a single-line Input.
      //
      // No maxRows on purpose. With a cap the box starts scrolling its own
      // content once the description passes that many lines, so a mouse wheel
      // over it scrolls the box instead of the page — the "disable mouse
      // scroll" complaint. Letting it grow to fit means it never has an
      // internal scrollbar to capture the wheel, and the whole description
      // stays visible.
      render: (value, record) => (
        <TextArea
          value={value}
          placeholder={t('Item description')}
          onChange={(e) => updateLineItem(record.key, 'description', e.target.value)}
          autoSize={{ minRows: 1 }}
          size="small"
        />
      )
    },
    {
      title: t('Account'),
      dataIndex: 'accountId',
      key: 'accountId',
      // Account labels are long ("430004 - Logistics Income - International
      // Freight"); 140px truncated nearly all of them.
      width: 240,
      render: (value, record) => (
        <Select
          placeholder={t('Select account')}
          value={value}
          onChange={(val) => updateLineItem(record.key, 'accountId', val)}
          style={{ width: '100%' }}
          size="small"
          showSearch
          filterOption={(input, option) => {
            const label = option?.label;
            return typeof label === 'string' ? label.toLowerCase().includes(input.toLowerCase()) : false;
          }}
          options={availableAccounts.map(account => ({
            value: account.id,
            label: `${account.code} - ${account.name}`,
          }))}
        />
      )
    },
    {
      title: t('Department'),
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 200,
      // Free text, not a picker. The client asked for this field to be unlinked
      // from the HR department list: whatever is typed is stored on the line and
      // nothing is created in HR. Lines saved before the change show their old
      // department name, resolved from the link, and keep it unless edited.
      render: (_: any, record: LineItem) => (
        <Input
          size="small"
          placeholder={t('Department')}
          value={record.departmentName ?? legacyDepartmentName(record.departmentId)}
          onChange={(e) => updateLineItem(record.key, 'departmentName', e.target.value)}
        />
      )
    },
    {
      title: t('Qty'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (value, record) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(val) => updateLineItem(record.key, 'quantity', val || 1)}
          style={{ width: '100%' }}
          size="small"
        />
      )
    },
    {
      title: t('Unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 120,
      render: (value, record) => (
        <Select
          value={value || 'unit(s)'}
          onChange={(val) => updateLineItem(record.key, 'unit', val)}
          style={{ width: '100%' }}
          size="small"
          optionLabelProp="label"
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                <Input
                  size="small"
                  placeholder={t('New unit')}
                  value={newUnitInput}
                  onChange={(e) => setNewUnitInput(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const trimmed = newUnitInput.trim();
                    if (trimmed && !unitOptions.includes(trimmed)) {
                      setUnitOptions([...unitOptions, trimmed]);
                      updateLineItem(record.key, 'unit', trimmed);
                      setNewUnitInput('');
                      // Persist to company settings so it survives reload + appears for others.
                      persistCompanyOption('unitOptions', trimmed);
                    }
                  }}
                />
              </div>
            </>
          )}
        >
          {unitOptions.map(u => (
            <Option key={u} value={u} label={u}>
              {renderOptionContent(u, DEFAULT_UNIT_OPTIONS, v => removeCompanyOption('unitOptions', v, setUnitOptions))}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: t('Unit Price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 150,
      render: (value, record) => (
        <InputNumber
          precision={2}
          prefix="$"
          value={value}
          onChange={(val) => updateLineItem(record.key, 'unitPrice', val ?? 0)}
          style={{ width: '100%' }}
          size="small"
        />
      )
    },
    {
      title: t('Discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 130,
      render: (_, record) => (
        <Space.Compact style={{ width: '100%' }}>
          <InputNumber
            value={record.discount || 0}
            onChange={(val) => updateLineItem(record.key, 'discount', val || 0)}
            min={0}
            style={{ width: '60%' }}
            size="small"
          />
          <Select
            value={record.discountType || 'fixed'}
            onChange={(val) => updateLineItem(record.key, 'discountType', val)}
            style={{ width: '40%' }}
            size="small"
          >
            <Option value="fixed">$</Option>
            <Option value="percentage">%</Option>
          </Select>
        </Space.Compact>
      )
    },
    {
      title: t('Line Total'),
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 100,
      align: 'right',
      render: (value) => (
        <span style={{ fontWeight: 500 }}>
          ${Number(value).toFixed(2)}
        </span>
      )
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeLineItem(record.key)}
          size="small"
        />
      )
    }
  ];

  const optionColumns: ColumnsType<OptionItem> = [
    {
      title: t('Template'),
      key: 'template',
      width: 150,
      render: (_, record) => (
        <Select
          placeholder={t('Select template')}
          allowClear
          showSearch
          optionFilterProp="children"
          onChange={(val) => val && handleOptionTemplateSelect(record.key, val)}
          style={{ width: '100%' }}
          size="small"
          filterOption={(input, option) =>
            ((option?.children as any) || '').toLowerCase().includes(input.toLowerCase())
          }
        >
          {templates.map(template => (
            <Option key={template.id} value={template.id}>
              {template.itemCode} - {template.description}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: t('Subcategory'),
      dataIndex: 'subcategory',
      key: 'subcategory',
      width: 140,
      render: (value, record) => (
        <Select
          value={value}
          onChange={(val) => updateOptionItem(record.key, 'subcategory', val)}
          placeholder={t('Subcategory')}
          style={{ width: '100%' }}
          allowClear
          size="small"
        >
          <Option value="Extra Storage">{t('Extra Storage')}</Option>
          <Option value="Value-added Service">{t('Value-added Service')}</Option>
        </Select>
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (value, record) => (
        <TextArea
          value={value}
          onChange={(e) => updateOptionItem(record.key, 'description', e.target.value)}
          placeholder={t('Option description')}
          autoSize={{ minRows: 1, maxRows: 4 }}
          size="small"
        />
      )
    },
    {
      title: t('Qty'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => updateOptionItem(record.key, 'quantity', val || 0)}
          min={0}
          style={{ width: '100%' }}
          size="small"
        />
      )
    },
    {
      title: t('Unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 120,
      render: (value, record) => (
        <Select
          value={value || 'unit(s)'}
          onChange={(val) => updateOptionItem(record.key, 'unit', val)}
          style={{ width: '100%' }}
          size="small"
          optionLabelProp="label"
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                <Input
                  size="small"
                  placeholder={t('New unit')}
                  value={newUnitInput}
                  onChange={(e) => setNewUnitInput(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const trimmed = newUnitInput.trim();
                    if (trimmed && !unitOptions.includes(trimmed)) {
                      setUnitOptions([...unitOptions, trimmed]);
                      updateOptionItem(record.key, 'unit', trimmed);
                      setNewUnitInput('');
                    }
                  }}
                />
              </div>
            </>
          )}
        >
          {unitOptions.map(u => (
            <Option key={u} value={u} label={u}>
              {renderOptionContent(u, DEFAULT_UNIT_OPTIONS, v => removeCompanyOption('unitOptions', v, setUnitOptions))}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: t('Unit Price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 150,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => updateOptionItem(record.key, 'unitPrice', val || 0)}
          min={0}
          style={{ width: '100%' }}
          prefix="$"
          size="small"
        />
      )
    },
    {
      title: t('Discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 130,
      render: (_, record) => (
        <Space.Compact style={{ width: '100%' }}>
          <InputNumber
            value={record.discount || 0}
            onChange={(val) => updateOptionItem(record.key, 'discount', val || 0)}
            min={0}
            style={{ width: '60%' }}
            size="small"
          />
          <Select
            value={record.discountType || 'percentage'}
            onChange={(val) => updateOptionItem(record.key, 'discountType', val)}
            style={{ width: '40%' }}
            size="small"
          >
            <Option value="fixed">$</Option>
            <Option value="percentage">%</Option>
          </Select>
        </Space.Compact>
      )
    },
    {
      title: t('Line Total'),
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 100,
      align: 'right',
      render: (value) => (
        <span style={{ fontWeight: 500 }}>
          ${Number(value || 0).toFixed(2)}
        </span>
      )
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeOptionItem(record.key)}
          size="small"
        />
      )
    }
  ];

  const itemsSubtotal = lineItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
  const optionsSubtotal = optionItems.reduce((sum, opt) => sum + Number(opt.lineTotal || 0), 0);

  return (
    <Modal
      title={invoice?.id ? t('Edit Invoice') : t('Create New Invoice')}
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {/* Row 1: Customer + Invoice No */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="customerId"
              label={t('Customer')}
              rules={[{ required: true, message: t('Please select a customer') }]}
            >
              <Select
                showSearch
                placeholder={t('Select a customer')}
                optionFilterProp="children"
                onChange={handleCustomerChange}
                filterOption={(input, option) => {
                  const children = option?.children as any;
                  const searchText = Array.isArray(children)
                    ? children.join(' ')
                    : String(children);
                  return searchText.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {customers.map(customer => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.name}
                    {customer.email && ` (${customer.email})`}
                    {!customer.email && customer.phone && ` (${customer.phone})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="invoiceNo"
              label={t('Invoice Number')}
              validateStatus={invoiceNoError ? 'error' : invoiceNoValidating ? 'validating' : ''}
              help={invoiceNoError || (!invoice && !customInvoiceNo ? 'Leave blank for auto-generation' : '')}
            >
              <Input
                // Show THIS company's format. The hint was hardcoded to the
                // Logistics format, so Shoptoplus Limited users were being told
                // to type INV/SL/... — the same confusion as the generator bug.
                placeholder={`e.g., ${
                  (accountingSettings?.invoicePrefix && accountingSettings.invoicePrefix !== 'INV-'
                    ? accountingSettings.invoicePrefix
                    : 'INV')
                }${dayjs().format('YYYY/MM')}/0001`}
                value={customInvoiceNo}
                onChange={handleInvoiceNoChange}
                disabled={invoice?.status !== 'DRAFT' && invoice?.status !== 'CONFIRM' && !!invoice}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="currency"
              label={t('Currency')}
              initialValue="HKD"
            >
              <Select
                value={selectedCurrency}
                onChange={handleCurrencyChange}
                style={{ width: '100%' }}
              >
                {CURRENCY_OPTIONS.map(currency => (
                  <Option key={currency.value} value={currency.value}>
                    {t(currency.label)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {selectedCurrency !== 'HKD' && (
            <Col span={6}>
              <Form.Item label={t('Exchange Rate (to HKD)')}>
                <InputNumber
                  value={exchangeRate}
                  onChange={(val) => setExchangeRate(val !== null && val !== undefined ? round4(Number(val)) : val)}
                  style={{ width: '100%' }}
                  precision={4}
                  min={0.0001}
                  step={0.0001}
                  placeholder={exchangeRateLoading ? t('Loading...') : t('Enter rate')}
                  addonAfter={exchangeRate && totalAmount ? `≈ HK$${(totalAmount * exchangeRate).toFixed(2)}` : undefined}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Row 2: Dates */}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="invoiceDate"
              label={t('Invoice Date')}
              rules={[{ required: true, message: t('Please select invoice date') }]}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="dueDate" label={t('Due Date')}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="billReference" label={t('Bill Reference')}>
              <Input placeholder={t('Enter bill reference / PO number / customer reference')} />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: Contact fields */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="contactPerson" label={t('Contact Person')}>
              <Input placeholder={t('Contact person name')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="contactEmail" label={t('Contact Email')}>
              <Input placeholder="contact@example.com" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="contactPhone" label={t('Contact Phone')}>
              <Input placeholder="+852 1234 5678" />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Address fields */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="deliveryAddress" label={t('Delivery Address')}>
              <TextArea rows={2} placeholder={t('Delivery address')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="billingAddress" label={t('Billing Address')}>
              <TextArea rows={2} placeholder={t('Billing address')} />
            </Form.Item>
          </Col>
        </Row>

        {/* Line Items */}
        <Divider>{t('Line Items')}</Divider>

        <Table
          columns={[lineItemsDragHandleColumn, ...lineItemColumns]}
          dataSource={lineItems}
          pagination={false}
          size="small"
          // Must match the sum of the column widths above (150+240+240+200
          // +100+120+150+130+100+50), otherwise antd squeezes columns below the widths
          // they declare — which is what kept Account and Unit Price cramped
          // even after widening them.
          scroll={{ x: 1480 }}
          style={{ marginBottom: 16 }}
          onRow={lineItemsRowDrag}
          footer={() => (
            <Button
              type="dashed"
              onClick={addLineItem}
              icon={<PlusOutlined />}
              block
            >
              {t('Add Line Item')}
                              </Button>
          )}
        />

        {/* Optional Services table hidden per accounting team request.
            Underlying data (optionItems state, options field in payload) is
            preserved so existing invoices that already have option rows stay
            intact; users just can't add new ones from this form. */}

        {/* Totals */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row justify="end">
            <Col span={10}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ marginBottom: 4 }}>
                  {t('Items Subtotal:')} {currencySymbol}{itemsSubtotal.toFixed(2)}
                </div>
                {optionItems.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    {t('Options Subtotal:')} {currencySymbol}{optionsSubtotal.toFixed(2)}
                  </div>
                )}
                <div style={{ fontSize: 16 }}>
                  <strong>{t('Total Amount:')} {currencySymbol}{Number(totalAmount || 0).toFixed(2)}</strong>
                  {selectedCurrency !== 'HKD' && exchangeRate && (
                    <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
                      ≈ HK${(Number(totalAmount || 0) * exchangeRate).toFixed(2)} {t('HKD (Rate:')} {exchangeRate})
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Payment Period + Payment Terms Text */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="paymentTerms" label={t('Payment Period')}>
              <Select
                placeholder={t('Select payment period')}
                allowClear
                optionLabelProp="label"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '4px 0' }} />
                    <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                      <Input
                        size="small"
                        placeholder={t('New payment period')}
                        value={newPaymentPeriodInput}
                        onChange={(e) => setNewPaymentPeriodInput(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          const trimmed = newPaymentPeriodInput.trim();
                          if (trimmed && !paymentPeriodOptions.includes(trimmed)) {
                            setPaymentPeriodOptions([...paymentPeriodOptions, trimmed]);
                            form.setFieldValue('paymentTerms', trimmed);
                            setNewPaymentPeriodInput('');
                            persistCompanyOption('paymentPeriodOptions', trimmed);
                          }
                        }}
                      />
                    </div>
                  </>
                )}
              >
                {paymentPeriodOptions.map(p => (
                  <Option key={p} value={p} label={p}>
                    {renderOptionContent(p, DEFAULT_PAYMENT_PERIOD_OPTIONS, v => removeCompanyOption('paymentPeriodOptions', v, setPaymentPeriodOptions))}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {!invoice && selectedCustomer && (
              <Form.Item>
                <label>
                  <input
                    type="checkbox"
                    checked={savePaymentTermForCustomer}
                    onChange={(e) => setSavePaymentTermForCustomer(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  {t('Save payment term for future invoices with')} {selectedCustomer.name}
                </label>
              </Form.Item>
            )}
          </Col>
          <Col span={16}>
            <Form.Item name="paymentTermsText" label={t('Payment Terms')}>
              <TextArea
                rows={3}
                placeholder={t('Detailed payment terms, bank account details, etc.')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Terms & Conditions */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="termsConditions" label={t('Terms & Conditions')}>
              <TextArea
                placeholder="e.g., Goods sold are not refundable, All disputes subject to Hong Kong law, etc."
                rows={3}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onClose}>
              {t('Cancel')}
                                      </Button>
          </Col>
          {!invoice && (
            <>
              <Col>
                <Button
                  onClick={() => handleSubmit(true)}
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  {t('Save as Draft')}
                                                  </Button>
              </Col>
              <Col>
                <Button
                  onClick={() => handleSubmitConfirm()}
                  loading={loading}
                  icon={<SaveOutlined />}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                >
                  {t('Save as Confirmed')}
                                                  </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => handleSubmit(false)}
                  loading={loading}
                  icon={<SendOutlined />}
                >
                  {t('Create & Send')}
                                                  </Button>
              </Col>
            </>
          )}
          {invoice && (
            <Col>
              <Button
                type="primary"
                onClick={() => handleSubmit(true)}
                loading={loading}
                icon={<SaveOutlined />}
              >
                {t('Update Invoice')}
                                            </Button>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default InvoiceForm;
